import React from "react";
import { ArrowDown, ArrowUp, GripVertical, Trash2 } from "lucide-react";
import { ActivityStepDraft } from "../activitySteps/activityStepTypes";
import { detectTimerDuration } from "../activitySteps/activityStepsParser";
import { validateActivityStep } from "../activitySteps/activityStepsValidation";
import { ActivityStepTypeSelect } from "./ActivityStepTypeSelect";

export function ActivityStepEditorCard({
  step,
  index,
  count,
  onChange,
  onRemove,
  onMove,
  onDropAt,
}: {
  step: ActivityStepDraft;
  index: number;
  count: number;
  onChange: (step: ActivityStepDraft) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  onDropAt: (fromIndex: number, toIndex: number) => void;
}) {
  const validation = validateActivityStep(step);
  const timer = step.type === "timer" ? detectTimerDuration(step.text) : null;
  const inputId = `activity-step-${step.id}`;
  const instructionLabel =
    step.type === "qa"
      ? "Question"
      : step.type === "upload"
        ? "Upload instruction"
        : "Instruction";

  return (
    <article
      className="cp-step-editor-card"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDrop={(event) => {
        event.preventDefault();
        const fromIndex = Number(event.dataTransfer.getData("text/plain"));
        if (Number.isInteger(fromIndex)) onDropAt(fromIndex, index);
      }}
    >
      <div className="cp-step-editor-card__header">
        <div className="cp-step-editor-card__title">
          <GripVertical aria-hidden="true" />
          <h3>Step {index + 1}</h3>
          <span
            className="cp-step-readiness"
            data-ready={validation.ready}
            role="status"
          >
            {validation.ready ? "Ready" : "Needs Info"}
          </span>
        </div>
        <div className="cp-step-editor-card__actions">
          <button
            type="button"
            className="cp-icon-button"
            aria-label={`Move step ${index + 1} up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cp-icon-button"
            aria-label={`Move step ${index + 1} down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cp-icon-button cp-icon-button--danger"
            aria-label={`Remove step ${index + 1}`}
            disabled={count === 1}
            onClick={onRemove}
          >
            <Trash2 aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="cp-step-editor-card__fields">
        <ActivityStepTypeSelect
          value={step.type}
          onChange={(type) => onChange({ ...step, type })}
        />
        <label htmlFor={inputId}>
          <span>{instructionLabel}</span>
          <textarea
            id={inputId}
            className="cp-form-input cp-form-textarea"
            rows={3}
            value={step.text}
            onChange={(event) => onChange({ ...step, text: event.target.value })}
          />
        </label>

        {step.type === "image" ? (
          <label htmlFor={`${inputId}-image`}>
            <span>Image name</span>
            <input
              id={`${inputId}-image`}
              className="cp-form-input"
              value={step.imageName}
              onChange={(event) =>
                onChange({ ...step, imageName: event.target.value })
              }
              placeholder="Example: toothpaste-amount"
            />
          </label>
        ) : null}

        {step.type === "video" ? (
          <label htmlFor={`${inputId}-url`}>
            <span>Video URL</span>
            <input
              id={`${inputId}-url`}
              className="cp-form-input"
              type="url"
              value={step.url}
              onChange={(event) => onChange({ ...step, url: event.target.value })}
              placeholder="https://"
            />
          </label>
        ) : null}

        {timer?.hasDuration ? (
          <p className="cp-step-timer-detection">
            Detected duration: <strong>{timer.label}</strong>
          </p>
        ) : null}
      </div>

      {!validation.ready ? (
        <ul className="cp-step-warnings" aria-label={`Step ${index + 1} warnings`}>
          {validation.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
